/**
 * GraphQL queries and hooks — businessCapability pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useAllPages } from './useAllPages';
import type {
  BusinessCapability,
  BusinessCapabilityConnection,
  BusinessCapabilityFilter,
} from '../types/businessCapability';

export const CAPABILITY_FIELDS = gql`
  fragment CapabilityFields on BusinessCapability {
    entityId
    entityType
    name
    description
    level
    parentCapabilityId
    criticality
    strategicImportance
    lifecycleStatus
    investmentPriority
    businessOwnerId
    owningBusinessUnitId
    stakeholderIds
    maturityLevel
    targetMaturityLevel
    currentInvestmentLevel
    targetInvestmentLevel
    planningHorizon
    notes
    assumptions
    tags
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const GET_CAPABILITY = gql`
  ${CAPABILITY_FIELDS}
  query GetCapability($id: ID!) {
    capability(id: $id) {
      ...CapabilityFields
      children {
        entityId
        name
        criticality
        lifecycleStatus
        level
      }
      supportingServices {
        entityId
        name
      }
      serviceCount
    }
  }
`;

export const LIST_CAPABILITIES = gql`
  ${CAPABILITY_FIELDS}
  query ListCapabilities($filter: BusinessCapabilityFilter, $first: Int, $after: String) {
    capabilities(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...CapabilityFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export function useCapability(id: string): QueryResult<{ capability: BusinessCapability | null }> {
  return useQuery(GET_CAPABILITY, {
    variables: { id },
    skip: !id,
  });
}

export function useCapabilities(
  filter?: BusinessCapabilityFilter,
  first: number = 250
): QueryResult<{ capabilities: BusinessCapabilityConnection }> {
  return useAllPages<{ capabilities: BusinessCapabilityConnection }>(
    LIST_CAPABILITIES,
    (data) => data?.capabilities,
    { filter },
    first
  );
}

export const MY_SERVICES_CAPABILITY_COVERAGE = gql`
  query MyServicesCapabilityCoverage {
    myServicesCapabilityCoverage {
      servedCapabilityIds
      totalServiceCount
    }
  }
`;

export interface MyServicesCapabilityCoverage {
  servedCapabilityIds: string[];
  totalServiceCount: number;
}

export function useMyServicesCapabilityCoverage(): QueryResult<{
  myServicesCapabilityCoverage: MyServicesCapabilityCoverage;
}> {
  return useQuery(MY_SERVICES_CAPABILITY_COVERAGE, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
